import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { resetPasswordSchema } from "../../utils/schemas/reset-password-schema"
import { usePost } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { Eye, EyeOff } from "lucide-react"
import { toastUtils, showSuccessToast } from "../../utils/toast"
import { useTranslation } from "../../i18n"

export default function ResetPasswordPage() {
  const { tObj } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tokenFromUrl = searchParams.get("token") || ""
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  const [statusMessage, setStatusMessage] = useState('')

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: tokenFromUrl,
      newPassword: "",
      confirmNewPassword: "",
    }
  })

  useEffect(() => {
    if (tokenFromUrl) setValue('token', tokenFromUrl)
  }, [tokenFromUrl, setValue])

  const { execute, loading } = usePost(endpoints.resetPassword, { isPublicRoute: true })

  const onSubmit = async (data) => {
    try {
      setStatus('loading')
      setStatusMessage(tObj.resetPassword.resetting())
      await execute({
        token: data.token,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      })
      setStatus('success')
      setStatusMessage(tObj.resetPassword.resetSuccess())
      showSuccessToast({ message: tObj.resetPassword.resetSuccessMessage() })
      setTimeout(() => navigate('/login', { replace: true }), 1500)
    } catch (e) {
      // Afficher un message d'erreur clair si l'API renvoie une erreur
      const message = e?.message || tObj.resetPassword.resetError()
      toastUtils.showErrorToast({ message })
      setStatus('error')
      setStatusMessage(message)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Effets de fond animés */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
      </div>

      <div className="w-full max-w-md bg-white/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 shadow-2xl border-0 relative z-10 hover:shadow-[0_0_50px_rgba(31,58,138,0.2)] transition-all duration-300">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-6 animate-gradient">
          {tObj.resetPassword.title()}
        </h1>

        {!tokenFromUrl && (
          <div className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-700 text-sm font-medium shadow-md">
            {tObj.resetPassword.invalidLink()}
          </div>
        )}

        {status !== 'idle' && (
          <div className={`mb-4 p-4 rounded-2xl text-sm font-medium shadow-md ${
            status === 'success' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-700' : 
            status === 'error' ? 'bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-700' : 
            'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 text-blue-700'
          }`}>
            {statusMessage}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <input type="hidden" {...register('token')} />
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              placeholder={tObj.resetPassword.newPasswordPlaceholder()}
              {...register('newPassword')}
              className={`w-full h-12 lg:h-14 bg-white/80 backdrop-blur-sm border-2 rounded-2xl text-gray-700 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:border-transparent px-4 pr-12 outline-none transition-all duration-300 shadow-sm hover:shadow-md ${
                errors.newPassword ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-[#203a8a] focus:border-[#203a8a]'
              }`}
            />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#203a8a] transition-colors duration-300">
              {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.newPassword && <p className="text-red-500 text-sm mt-1 px-1">{errors.newPassword.message}</p>}
          </div>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder={tObj.resetPassword.confirmPasswordPlaceholder()}
              {...register('confirmNewPassword')}
              className={`w-full h-12 lg:h-14 bg-white/80 backdrop-blur-sm border-2 rounded-2xl text-gray-700 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:border-transparent px-4 pr-12 outline-none transition-all duration-300 shadow-sm hover:shadow-md ${
                errors.confirmNewPassword ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-[#203a8a] focus:border-[#203a8a]'
              }`}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#203a8a] transition-colors duration-300">
              {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.confirmNewPassword && <p className="text-red-500 text-sm mt-1 px-1">{errors.confirmNewPassword.message}</p>}
          </div>
          <button 
            type="submit" 
            disabled={isSubmitting || loading || !tokenFromUrl} 
            className="w-full h-12 lg:h-14 bg-gradient-to-r from-[#203a8a] to-[#2563eb] hover:from-[#1a1f4a] hover:to-[#1e40af] disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
          >
            {isSubmitting || loading ? tObj.resetPassword.resettingButton() : tObj.resetPassword.resetButton()}
          </button>
        </form>
      </div>
    </div>
  )
}


